import type { HolisticSummary } from '@fruitful/types';
import { useAtomValue } from 'jotai';
import * as React from 'react';
import { useMemo, useState } from 'react';
import Animated, { FadeIn, LinearTransition } from 'react-native-reanimated';
import {
    BodyLargeEmphasized,
    BodyMediumRegular,
    BodySmallRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    InfoButton,
    isWeb,
    LinkIcon,
    LoadingSkeleton,
    PillButton,
    Private,
    RotateCcwIcon,
    Sheet,
    Spinner,
    View,
    XStack,
    YStack,
} from 'smoothie';

import { AddInstitutionSheet } from '../../../common/components/AddInstitutionSheet';
import { formatDollars } from '../../../common/utils/bankingUtils';
import { getHolisticAssetsTotalDollars } from '../../../common/utils/holisticUtils';
import { IS_DEV } from '../../../config/FruitfulEnvironment';
import { LINKED_ACCOUNT_BALANCE_NOTE_VERBOSE } from '../../../holistic/constants';
import useConnectQuilttInstitution from '../../../holistic/hooks/useConnectQuilttInstitution';
import useGetHolisticSummary from '../../../holistic/hooks/useGetHolisticSummary';
import { holisticAccountsAtom } from '../../../holistic/state/HolisticAtoms';
import LinkedAccounts from './LinkedAccounts';

const AccountCategories = ({
    holisticSummary,
    isRefetchingHolisticSummary,
    holisticSummaryHasError,
    refetchHolisticSummary,
}: {
    holisticSummary: HolisticSummary | undefined;
    isRefetchingHolisticSummary: boolean;
    holisticSummaryHasError: boolean;
    refetchHolisticSummary: () => void;
}) => {
    const hasHolisticData = holisticSummary != null;

    if (holisticSummaryHasError) {
        return (
            <YStack
                alignItems="center"
                justifyContent="center"
                marginVertical={36}
                gap={16}
            >
                <BodyMediumRegular color="$gray1000">
                    Whoops, our account link provider hit an error
                </BodyMediumRegular>
                <PillButton
                    text="Try again"
                    onPress={() => {
                        refetchHolisticSummary();
                    }}
                />
            </YStack>
        );
    }

    if (!hasHolisticData) {
        return <Spinner height={96} />;
    }

    const showLinkedAccountCategories = hasHolisticData;

    return (
        <>
            {showLinkedAccountCategories && (
                <LinkedAccounts
                    isRefetchingHolisticSummary={isRefetchingHolisticSummary}
                    holisticSummary={holisticSummary}
                />
            )}
        </>
    );
};

const Header = ({
    holisticSummary,
    isRefetchingHolisticSummary,
    holisticSummaryHasError,
}: {
    holisticSummary: HolisticSummary | undefined;
    isRefetchingHolisticSummary: boolean;
    holisticSummaryHasError: boolean;
}) => {
    const title = 'Your money';
    const holisticAccountsCount = useAtomValue(holisticAccountsAtom).length;
    const hasHolisticAccounts = holisticAccountsCount > 0;

    const [learnMoreSheetOpen, setLearnMoreSheetOpen] = useState(false);

    const assetsTotalDollars = useMemo(() => {
        return getHolisticAssetsTotalDollars(holisticSummary);
    }, [holisticSummary]);

    const formattedTotalAssets =
        assetsTotalDollars !== null ? formatDollars(assetsTotalDollars) : null;

    return (
        <View flex={1}>
            <XStack
                justifyContent="space-between"
                alignItems="center"
                padding={8}
            >
                <YStack gap={4}>
                    <BodyLargeEmphasized>{title}</BodyLargeEmphasized>
                    {!hasHolisticAccounts || holisticSummaryHasError ? (
                        <></>
                    ) : assetsTotalDollars == null ||
                      isRefetchingHolisticSummary ? (
                        <LoadingSkeleton
                            paddingVertical={4}
                            height={34}
                            width={140}
                        />
                    ) : (
                        <Private>
                            <Animated.View entering={FadeIn.duration(300)}>
                                <BodySmallRegular color="$gray1000">
                                    {holisticAccountsCount} accounts ·{' '}
                                    {formattedTotalAssets}
                                </BodySmallRegular>
                            </Animated.View>
                        </Private>
                    )}
                </YStack>
                {hasHolisticAccounts && (
                    <InfoButton
                        onPress={() => {
                            setLearnMoreSheetOpen(true);
                        }}
                    />
                )}
            </XStack>

            {hasHolisticAccounts && (
                <Private>
                    <Sheet
                        title={title}
                        subtitle={formattedTotalAssets ?? undefined}
                        isOpen={learnMoreSheetOpen}
                        onClose={() => {
                            setLearnMoreSheetOpen(false);
                        }}
                        showCloseButton={false}
                        forceKeyboardCompatibleBottomSheet
                    >
                        <YStack paddingTop={12} gap={50} $sm={{ gap: 80 }}>
                            <BodyMediumRegular>{`This is the total of your linked Spend, Save, and Invest accounts. \n\n${LINKED_ACCOUNT_BALANCE_NOTE_VERBOSE}`}</BodyMediumRegular>
                            <Button
                                variant={ButtonVariant.PRIMARY}
                                text="Got it"
                                onPress={() => {
                                    setLearnMoreSheetOpen(false);
                                }}
                            />
                        </YStack>
                    </Sheet>
                </Private>
            )}
        </View>
    );
};

const YourMoney = () => {
    const { connectInstitution } = useConnectQuilttInstitution();
    const [addInstitutionSheetOpen, setAddInstitutionSheetOpen] =
        useState(false);

    const {
        data: holisticSummary,
        isRefetching: isRefetchingHolisticSummary,
        isError: holisticSummaryHasError,
        refetch: refetchHolisticSummary,
    } = useGetHolisticSummary();

    return (
        <Animated.View
            style={{
                width: '100%',
            }}
            {...(isWeb
                ? { entering: FadeIn.duration(500) }
                : {
                      entering: FadeIn.duration(500),
                      layout: LinearTransition.duration(500),
                  })}
        >
            <YStack
                width="100%"
                backgroundColor="$gray200"
                borderRadius={24}
                padding={16}
                gap={16}
            >
                <Header
                    holisticSummary={holisticSummary}
                    isRefetchingHolisticSummary={isRefetchingHolisticSummary}
                    holisticSummaryHasError={holisticSummaryHasError}
                />
                <AccountCategories
                    holisticSummary={holisticSummary}
                    isRefetchingHolisticSummary={isRefetchingHolisticSummary}
                    holisticSummaryHasError={holisticSummaryHasError}
                    refetchHolisticSummary={() => {
                        void refetchHolisticSummary();
                    }}
                />
                <Button
                    variant={ButtonVariant.SECONDARY}
                    size={ButtonSize.LARGE}
                    text="Link more accounts"
                    icon={LinkIcon}
                    onPress={() => {
                        setAddInstitutionSheetOpen(true);
                    }}
                />
                {IS_DEV && (
                    <PillButton
                        text="Refresh Holistic Data (Dev)"
                        onPress={() => {
                            void refetchHolisticSummary();
                        }}
                        icon={RotateCcwIcon}
                    />
                )}
            </YStack>
            <AddInstitutionSheet
                isOpen={addInstitutionSheetOpen}
                onClose={() => {
                    setAddInstitutionSheetOpen(false);
                }}
                launchInstitutionConnector={() => {
                    setAddInstitutionSheetOpen(false);
                    connectInstitution();
                }}
            />
        </Animated.View>
    );
};

export default YourMoney;
